import {
  buildAnnouncementKey,
  effectiveCallEndTime,
  readDismissedReminders,
  useUpcomingCallsQuery,
} from './index'
import { secondsToMs } from '@thyme/libs/src/time/convert'
import { DateTime } from 'luxon'
import { onMounted, onUnmounted, ref, watch } from 'vue'
import type { TimedUpcomingCall, UpcomingCall } from './types'

// How often we re-check the clock against each call's alert time.
const TICK_INTERVAL_SECONDS = 30

/**
 * How long after a call ends we wait before warning it was never attempted.
 */
const MISSED_CALL_GRACE_MINUTES = 120

/**
 * @param call
 */
export function missedAlertTime(call: TimedUpcomingCall): DateTime {
  return effectiveCallEndTime(call).plus({
    minutes: MISSED_CALL_GRACE_MINUTES,
  })
}

/**
 * Currently our query does not receive calls that have
 * been dispositioned in any way, so don't need to filter
 * @param call
 * @param now
 */
export function isMissedCall(
  call: UpcomingCall,
  now: DateTime
): call is TimedUpcomingCall {
  if (call.isDateOnly) return false
  return missedAlertTime(call).toMillis() <= now.toMillis()
}

/**
 * Shares `useUpcomingCallsQuery` with the reminder loop
 * Known gap:
 * - the query fetches the local day, so a call ending at 11:30pm has its 1:30am alert
 *   land after the day rolls over and the call has left the list. Late night missed calls
 *   therefore never alert missed. Edge case.
 * @param onMissedCalls called with the calls that were missed, never with an empty array
 */
export function useMissedCallAlerts(
  onMissedCalls: (calls: TimedUpcomingCall[]) => void
): void {
  const day = ref(DateTime.now())
  const { data: calls, refetch } = useUpcomingCallsQuery(day)
  const announced = new Set<string>()
  let tickHandle: ReturnType<typeof setInterval> | undefined
  let checking = false

  /**
   * @param now
   */
  function findMissedCalls(now: DateTime): TimedUpcomingCall[] {
    const dismissed = readDismissedReminders()

    return (calls.value ?? []).filter((call): call is TimedUpcomingCall => {
      if (!isMissedCall(call, now)) return false
      const announcementKey = buildAnnouncementKey(call, 'missed')
      return !announced.has(announcementKey) && !dismissed.has(announcementKey)
    })
  }

  /**
   * Announces any calls whose grace period has just run out.
   * Note that it can be called twice if refetch() returns changed 'calls' data (triggering watch())
   * But a second call will short-circuit at either 'if (checking) return' or when findMissedCalls returns []
   */
  async function checkForMissedCalls() {
    if (checking) return
    const now = DateTime.now()
    // Only reassign on a date change, so the query key stays stable within a day
    if (!now.hasSame(day.value, 'day')) day.value = now

    // Cheap pass over the cache first, only refetch to confirm correct missed call data
    if (!findMissedCalls(now).length) return

    checking = true
    try {
      await refetch()
    } finally {
      checking = false
    }

    const missed = findMissedCalls(DateTime.now())
    if (!missed.length) return

    for (const call of missed) {
      announced.add(buildAnnouncementKey(call, 'missed'))
    }
    onMissedCalls(missed)
  }

  watch(calls, () => {
    void checkForMissedCalls()
  })

  onMounted(() => {
    void checkForMissedCalls()
    tickHandle = setInterval(() => {
      void checkForMissedCalls()
    }, secondsToMs(TICK_INTERVAL_SECONDS))
  })

  onUnmounted(() => {
    clearInterval(tickHandle)
  })
}
